import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, isSameDay } from "date-fns";
import { formatInTimeZone } from 'date-fns-tz';
import chineseLunar from 'chinese-lunar';
import toast from 'react-hot-toast';
import {
  useMediaQuery,
  useTheme, Box,
  Typography,
  CircularProgress,
  Button,
  Checkbox,
  Popover,
} from '@mui/material';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import useAuthCheck from 'state/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT, DASHBOARD_TIMER_INTERVAL } from 'state/constants';
import api from 'state/axios';
import { fetchLegMembersByFlt } from 'state/api';
import { addTimeToDate } from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import FireworksComponent from 'components/Fireworks';
import CrewListDialog from 'components/CrewListDialog';
import InvoiceDialog from 'scenes/inflightSales/InvoiceDialog';


const TIMER_MS = Number(DASHBOARD_TIMER_INTERVAL);

const convertLunarToSolar = (lunarDate) => {
  if (!lunarDate) return null;
  const [year, month, day] = lunarDate.split('-').map(Number);
  return chineseLunar.lunarToSolar(year, month, day);
};

const getTime = (f, timeMode, field) =>
  timeMode === 'UTC' ? f[field] : f[`${field}LocalBase`];

const groupAndSortLegs = (legs) => {
  if (!legs?.length) return {};

  const grouped = legs.reduce((acc, leg, index) => {
    const key = leg.routeNo
      ? `${leg.routeDate}-${leg.routeNo}`
      : `${leg.flightDate}-${leg.etd}-${index}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(leg);
    return acc;
  }, {});

  return Object.keys(grouped)
    .sort((a, b) => a.localeCompare(b))
    .reduce((sorted, key) => {
      sorted[key] = grouped[key];
      return sorted;
    }, {});
};

const compareCrewCodes = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  const codes1 = arr1.map(c => c.crewCode).sort();
  const codes2 = arr2.map(c => c.crewCode).sort();
  return codes1.every((code, i) => code === codes2[i]);
};

const CrewNamePopover = ({ anchorEl, crewName, onClose }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    disableScrollLock
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
  >
    <Box sx={{ padding: 1 }}>
      <Typography>{crewName}</Typography>
    </Box>
  </Popover>
);


const Dashboard = () => {
  useAuthCheck();
  const user = useSelector((state) => state.auth.user);
  const timeMode = useSelector((state) => state.auth.timemode);
  const params = useSelector((state) => state.auth.params);
  const crewRanks = useSelector((state) => state.auth.crewRanks);
  const crewRosters = useSelector((state) => state.auth.crewFlights);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollRef = useRef(null);
  const { startDate, endDate } = useDateRangeContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFireworks, setShowFireworks] = useState(false);
  const [holidays, setHolidays] = useState([]);

  const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
  const [flight, setFlight] = useState([]);
  const [crewList, setCrewList] = useState([]);

  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [popoverInfo, setPopoverInfo] = useState({ anchorEl: null, crewName: '' });

  const [selectedFlights, setSelectedFlights] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [invoicedFlights, setInvoicedFlights] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [checkboxesDisabled, setCheckboxesDisabled] = useState(true);

  const paramsMap = useMemo(
    () => new Map(params?.map(p => [p.param, p.value]) ?? []),
    [params]
  );
  const IATA_AIRLINE_CODE = paramsMap.get("IATA_AIRLINE_CODE") || "QH";   
  const INVOICE_OFFSET_HOURS = Number(paramsMap.get("INVOICE_OFFSET_HOURS")) || 120;   
  
  const cabinCrewRanks = useMemo(
    () => crewRanks.filter(r => r.rankType === 'Cabin Crew').map(r => r.rankCode),
    [crewRanks]
  );

  useEffect(() => {
    if (!startDate || !endDate) return;

    const payload = { startDate, endDate };

    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);
        await Promise.all([
          api.get('/holidays/active').then(res => setHolidays(res.data)),
          api.post('/inflightsales/invoicedflights', payload).then(res => setInvoicedFlights(res.data)),
          api.post('/inflightsales/invoices', payload).then(res => setInvoices(res.data)),
        ]);
        setCheckboxesDisabled(false);
      } catch (err) {
        setError(err);
        toast.error(err.message || "Failed to fetch data!");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, TIMER_MS);
    return () => clearInterval(interval);
  }, [startDate, endDate]);

  const isHolidayActiveToday = useMemo(() => {
    const today = new Date();
    return holidays.some(holiday => {
      if (!holiday.isActive) return false;

      let start = new Date(holiday.startDate);
      let end = holiday.endDate ? new Date(holiday.endDate) : start;

      if (holiday.isLunar) {
        const solarStart = convertLunarToSolar(format(start, 'yyyy-MM-dd'));
        const solarEnd = convertLunarToSolar(format(end, 'yyyy-MM-dd'));
        if (!solarStart) return false;
        start = solarStart;
        end = solarEnd;
      }

      switch (holiday.frequency) {
        case 'once':
          return isSameDay(today, start) || (start <= today && end >= today);
        case 'weekly':
          return today.getDay() === start.getDay();
        case 'monthly':
          return today.getDate() === start.getDate();
        case 'yearly':
          return today.getMonth() === start.getMonth() && today.getDate() === start.getDate();
        default:
          return false;
      }
    });
  }, [holidays]);

  useEffect(() => {
    if (loading) return;
    if (isHolidayActiveToday) {
      setShowFireworks(true);
      const timeout = setTimeout(() => setShowFireworks(false), 9000);
      return () => clearTimeout(timeout);
    } else {
      setShowFireworks(false);
    }
  }, [isHolidayActiveToday, loading]);

  const handlePopoverOpen = useCallback((event, crewName) => {
    event.preventDefault();
    event.stopPropagation();
    if (crewName) setPopoverInfo({ anchorEl: event.currentTarget, crewName });
  }, []);

  const handlePopoverClose = useCallback(() => {
    setPopoverInfo({ anchorEl: null, crewName: '' });
  }, []);

  const crewRoutes = useMemo(() => groupAndSortLegs(crewRosters), [crewRosters]);

  const handleCheckboxChange = useCallback(async (f) => {
    const now = new Date();
    if (now - new Date(addTimeToDate(f.flightDate, f.etd)) > INVOICE_OFFSET_HOURS * 60 * 60 * 1000) {
      toast.error(`Not allowed if exceed ${INVOICE_OFFSET_HOURS} hours`);
      return;
    }

    const result = await fetchLegMembersByFlt(f.flightDate, f.flightDesc, f.dep);
    if (!result) return;

    const legCrews = result.legMembers?.filter(m => cabinCrewRanks.includes(m.post) && !m.pax) ?? [];

    setSelectedFlights((prevSelected) => {
      if (prevSelected.includes(f)) {
        const remaining = prevSelected.filter(flt => flt !== f);
        if (remaining.length === 0) setCrewMembers([]);
        return remaining;
      }

      if (prevSelected.length > 0 && prevSelected[0].sectorType !== f.sectorType) {
        toast.error("Domestic & International flights cannot be selected together.");
        return prevSelected;
      }

      if (crewMembers.length === 0) {
        setCrewMembers(legCrews);
      } else if (!compareCrewCodes(legCrews, crewMembers)) {
        toast.error("Cannot select flights with different crew lists.");
        return prevSelected;
      }

      if (prevSelected.some(flt =>
        flt.flightDate === f.flightDate &&
        flt.flightDesc === f.flightDesc &&
        flt.dep === f.dep
      )) {
        return prevSelected;
      }

      return [...prevSelected, f];
    });
  }, [
    INVOICE_OFFSET_HOURS, 
    cabinCrewRanks, 
    crewMembers
  ]);

  const isCheckboxDisabled = useCallback((f) => (
    checkboxesDisabled ||
    !!f.pax ||
    new Date(f.flightDate) > new Date() ||
    invoicedFlights.some(inv =>
      inv.flightDate === f.flightDate &&
      (inv.flightDesc || `${IATA_AIRLINE_CODE}${inv.flightNo}`) === f.flightDesc &&
      inv.dep === f.dep
    )
  ), [
    IATA_AIRLINE_CODE,
    checkboxesDisabled, 
    invoicedFlights, 
  ]);

  const handleInvoiceClick = useCallback(() => {
    if (selectedFlights.length > 0) setOpenInvoiceDialog(true);
  }, [selectedFlights.length]);

  const handleInvoiceSaved = useCallback((flights) => {
    setInvoicedFlights(prev => [...prev, ...flights]);
  }, []);

  const handleCloseInvoiceDialog = useCallback(() => {
    setSelectedFlights([]);
    setCrewMembers([]);
    setOpenInvoiceDialog(false);
  }, []);

  const handleViewClick = useCallback(async (f) => {
    try {
      const result = await fetchLegMembersByFlt(f.flightDate, f.flightDesc, f.dep);
      if (!result) return;
      const { flightDetails, legMembers } = result;
      setFlight(flightDetails);
      setCrewList(legMembers.filter(m => cabinCrewRanks.includes(m.post) && !m.pax));
      setOpenCrewListDialog(true);
    } catch (err) {
      setError(err);
      setOpenCrewListDialog(false);
    }
  }, [cabinCrewRanks]);

  const handleCloseCrewListDialog = useCallback(() => {
    setCrewList([]);
    setOpenCrewListDialog(false);
  }, []);

  if (loading) return (
    <FlexCenter height={LAYOUT_HEIGHT}>
      <CircularProgress />
    </FlexCenter>
  );
  
  if (error) return (
    <FlexCenter height={LAYOUT_HEIGHT}>
      <Typography color="error">{error?.message}</Typography>
    </FlexCenter>
  );

  if (Object.keys(crewRoutes).length === 0) return (
    <FlexCenter height={LAYOUT_HEIGHT}>
      <Typography color="error">No flights available</Typography>
    </FlexCenter>
  )


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Dashboard"
          subtitle={loading ? 'Loading...' : 'Crew Schedule'}
        />
      </FlexBetween>
      
      <Box 
        display="grid" 
        gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
        gap={2}
      >
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}  
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              borderRadius: 1,
              border: `1px solid`,
              borderColor: "neutral.deep",
              gridColumn: "span 12",
              overflowY: "auto",
            }}
          >
            {Object.entries(crewRoutes)?.map(([key, flights]) => (
              <Box 
                key={key} 
                px={1} 
                borderBottom={`1px solid ${theme.palette.neutral.dark}`}
              >
                {flights.map((f, idx) => {
                  const filteredInvoice = invoices.find((invoice) =>
                    invoice.selectedFlights.some(
                      (sf) =>
                        sf.flightDate === f.flightDate &&
                        sf.flightDesc === f.flightDesc &&
                        sf.dep === f.dep
                    )
                  );

                  return (
                    <FlexBetween key={idx} py={0.5} gap={1}>
                      {filteredInvoice?.crewName 
                        ? (
                          <Typography
                            width="18px"
                            textAlign="left"
                            color="primary"
                            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={(e) => handlePopoverOpen(e, filteredInvoice.crewName)}
                            onTouchStart={(e) => handlePopoverOpen(e, filteredInvoice.crewName)}
                          >
                            {f.flightNo}
                          </Typography>
                        ) : (
                          <Typography width="18px" textAlign="center" color="primary">
                            {f.flightNo}
                          </Typography>
                        )
                      }
                      <Typography width={isMobile ? "26px" : "40px"}>
                        {timeMode === 'UTC'
                          ? formatInTimeZone(f.fullEtd || f.flightDate, 'UTC', isMobile ? 'ddMMM' : 'ddMMMyy')
                          : formatInTimeZone(f.fullEtdLocalBase || f.flightDateLocalBase, 'UTC', isMobile ? 'ddMMM' : 'ddMMMyy')
                        }
                      </Typography>
                      <Typography width="20px" textAlign="center">
                        {getTime(f, timeMode, 'etd')}
                      </Typography>
                      <Typography width="16px" textAlign="center">
                        {f.dep}
                      </Typography>
                      <Typography width="16px" textAlign="center">
                        {f.arr}
                      </Typography>
                      <Typography width="20px" textAlign="center">
                        {getTime(f, timeMode, 'eta')}
                      </Typography>
                      <Typography width="4px" textAlign="center">{f.pax}</Typography>
                      <Checkbox
                        color="primary"
                        checked={selectedFlights.includes(f)}
                        onChange={() => handleCheckboxChange(f)}
                        disabled={isCheckboxDisabled(f)}
                        sx={{ p: 0.5 }}
                      />
                      <PersonPinIcon
                        aria-label="View"
                        onClick={() => handleViewClick(f)}
                        sx={{
                          color: "primary.dark",
                          cursor: "pointer",
                          transition: "transform 0.15s, opacity 0.15s",
                          "&:hover": { transform: "scale(1.1)", opacity: 0.8 },
                        }}
                      />
                    </FlexBetween>
                  );
                })}
              </Box>
            ))}
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="secondary"
            disabled={loading || selectedFlights.length === 0 || crewMembers.length === 0}
            onClick={handleInvoiceClick}
            sx={{ gridColumn: "span 12", height: 42, flexShrink: 0 }}
          >
            INVOICE
          </Button>

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>

      {/* Single popover instance rendered once outside */}
      <CrewNamePopover
        anchorEl={popoverInfo.anchorEl}
        crewName={popoverInfo.crewName}
        onClose={handlePopoverClose}
      />

      {showFireworks && <FireworksComponent />}

      {openInvoiceDialog &&
        <InvoiceDialog
          open={openInvoiceDialog}
          onClose={handleCloseInvoiceDialog}
          selectedInvoiceId={null}
          crewCode={user.crewCode}
          crewName={user.crewName}
          selectedFlights={selectedFlights}
          crewMembers={crewMembers}
          selectedProducts={null}
          onInvoiceSaved={handleInvoiceSaved}
        />
      }

      {openCrewListDialog &&
        <CrewListDialog
          open={openCrewListDialog}
          onClose={handleCloseCrewListDialog}
          flight={flight}
          crewList={crewList}
          jumpList={null}
        />
      }
    </Box>
  );
};

export default Dashboard;
